import { z } from 'zod';
/**
 * The AG-UI event types the platform emits.
 *
 * This is a subset of the protocol's event catalogue; state, activity, and snapshot events
 * have no platform equivalent yet.
 *
 * These members are SCREAMING_SNAKE_CASE rather than the UpperCamelCase every other enum in the
 * codebase uses. They are not names we chose: they are the literal wire values AG-UI clients
 * match on, so renaming them would make the stream unreadable to the protocol's own libraries.
 * Anything the platform names for itself, including the run error codes, stays conventional.
 *
 * @see https://docs.ag-ui.com/sdk/js/core/events
 */
export const AgenticPlatformEventTypeSchema = z.enum([
    'CUSTOM',
    'REASONING_END',
    'REASONING_MESSAGE_CONTENT',
    'REASONING_MESSAGE_END',
    'REASONING_MESSAGE_START',
    'REASONING_START',
    'RUN_ERROR',
    'RUN_FINISHED',
    'RUN_STARTED',
    'TEXT_MESSAGE_CONTENT',
    'TEXT_MESSAGE_END',
    'TEXT_MESSAGE_START',
    'TOOL_CALL_ARGS',
    'TOOL_CALL_END',
    'TOOL_CALL_RESULT',
    'TOOL_CALL_START',
]);
function createAgenticPlatformEventSchema(type, shape) {
    return z.object({
        ...shape,
        timestamp: z.number().optional(),
        type: z.literal(type),
    });
}
export const AgenticPlatformRunStartedEventSchema = createAgenticPlatformEventSchema('RUN_STARTED', {
    runId: z.string(),
    threadId: z.string(),
});
export const AgenticPlatformRunFinishedEventSchema = createAgenticPlatformEventSchema('RUN_FINISHED', {
    result: z.json().optional(),
    runId: z.string(),
    threadId: z.string(),
});
export const AgenticPlatformRunErrorEventSchema = createAgenticPlatformEventSchema('RUN_ERROR', {
    code: z.string().optional(),
    message: z.string(),
});
export const AgenticPlatformTextMessageStartEventSchema = createAgenticPlatformEventSchema('TEXT_MESSAGE_START', {
    messageId: z.string(),
    role: z.literal('assistant'),
});
export const AgenticPlatformTextMessageContentEventSchema = createAgenticPlatformEventSchema('TEXT_MESSAGE_CONTENT', {
    delta: z.string().min(1),
    messageId: z.string(),
});
export const AgenticPlatformTextMessageEndEventSchema = createAgenticPlatformEventSchema('TEXT_MESSAGE_END', {
    messageId: z.string(),
});
export const AgenticPlatformReasoningStartEventSchema = createAgenticPlatformEventSchema('REASONING_START', {
    messageId: z.string(),
});
export const AgenticPlatformReasoningEndEventSchema = createAgenticPlatformEventSchema('REASONING_END', {
    messageId: z.string(),
});
export const AgenticPlatformReasoningMessageStartEventSchema = createAgenticPlatformEventSchema('REASONING_MESSAGE_START', { messageId: z.string(), role: z.literal('reasoning') });
export const AgenticPlatformReasoningMessageContentEventSchema = createAgenticPlatformEventSchema('REASONING_MESSAGE_CONTENT', { delta: z.string().min(1), messageId: z.string() });
export const AgenticPlatformReasoningMessageEndEventSchema = createAgenticPlatformEventSchema('REASONING_MESSAGE_END', {
    messageId: z.string(),
});
export const AgenticPlatformToolCallStartEventSchema = createAgenticPlatformEventSchema('TOOL_CALL_START', {
    parentMessageId: z.string().optional(),
    toolCallId: z.string(),
    toolCallName: z.string(),
});
export const AgenticPlatformToolCallArgsEventSchema = createAgenticPlatformEventSchema('TOOL_CALL_ARGS', {
    delta: z.string(),
    toolCallId: z.string(),
});
export const AgenticPlatformToolCallEndEventSchema = createAgenticPlatformEventSchema('TOOL_CALL_END', {
    toolCallId: z.string(),
});
export const AgenticPlatformToolCallResultEventSchema = createAgenticPlatformEventSchema('TOOL_CALL_RESULT', {
    content: z.string(),
    messageId: z.string(),
    role: z.literal('tool').optional(),
    toolCallId: z.string(),
});
/** Platform extensions that have no dedicated AG-UI event. */
export const AgenticPlatformCustomEventSchema = createAgenticPlatformEventSchema('CUSTOM', {
    name: z.string(),
    value: z.json(),
});
export const AgenticPlatformEventSchema = z.discriminatedUnion('type', [
    AgenticPlatformCustomEventSchema,
    AgenticPlatformReasoningEndEventSchema,
    AgenticPlatformReasoningMessageContentEventSchema,
    AgenticPlatformReasoningMessageEndEventSchema,
    AgenticPlatformReasoningMessageStartEventSchema,
    AgenticPlatformReasoningStartEventSchema,
    AgenticPlatformRunErrorEventSchema,
    AgenticPlatformRunFinishedEventSchema,
    AgenticPlatformRunStartedEventSchema,
    AgenticPlatformTextMessageContentEventSchema,
    AgenticPlatformTextMessageEndEventSchema,
    AgenticPlatformTextMessageStartEventSchema,
    AgenticPlatformToolCallArgsEventSchema,
    AgenticPlatformToolCallEndEventSchema,
    AgenticPlatformToolCallResultEventSchema,
    AgenticPlatformToolCallStartEventSchema,
]);
/** The `CUSTOM` event name carrying incremental output from a long-running platform tool. */
export const AGENTIC_PLATFORM_TOOL_CALL_PARTIAL_RESULT_EVENT_NAME = 'toolCallPartialResult';
export const AgenticPlatformToolCallPartialResultSchema = z.object({
    partialResult: z.json(),
    toolCallId: z.string(),
    toolCallName: z.string(),
});
//# sourceMappingURL=event.js.map